// pages/admin/RewardSettingsManagement.tsx

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Tooltip,
  Breadcrumbs,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Grid,
  InputAdornment,
  Divider,
  Paper,
  Chip,
  Switch,
  FormControlLabel,
  Dialog as ConfirmDialog,
  DialogTitle as ConfirmDialogTitle,
  DialogContent as ConfirmDialogContent,
  DialogActions as ConfirmDialogActions,
} from '@mui/material';
import {
  Delete,
  Edit,
  Add,
  Refresh,
  NavigateNext,
  Home,
  Close,
  Search,
  Settings as SettingsIcon,
  Stars,
  MonetizationOn,
  CurrencyExchange,
  CheckCircle,
  Star,
  StarBorder,
  Warning,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListRewardSettingsQuery,
  useAddRewardSettingMutation,
  useUpdateRewardSettingMutation,
  useDeleteRewardSettingMutation,
  useSetDefaultRewardSettingMutation,
  RewardSetting,
  RewardSettingFormData,
  validateAmount,
  validatePoints,
} from '../../features/admin/rewardSettingsApi.ts';

// Shopify-like color palette
const shopifyColors = {
  primary: '#008060',
  primaryLight: '#00a47c',
  primaryDark: '#006e52',
  background: '#f6f6f7',
  surface: '#ffffff',
  border: '#e1e3e5',
  textPrimary: '#202223',
  textSecondary: '#6d7175',
  success: '#008060',
  warning: '#ffc453',
  error: '#d72c0d',
  blue: '#2c6ecb',
  purple: '#5c6ac4',
  gold: '#ffd700',
};

// Currency symbols mapping
const currencySymbols: { [key: string]: string } = {
  'AED': 'د.إ',
  'USD': '$',
  'EUR': '€',
  'GBP': '£',
  'SAR': 'ر.س',
  'QAR': 'ر.ق',
  'KWD': 'د.ك',
};

const RewardSettingsManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  // Filter states
  const [searchStr, setSearchStr] = useState('');

  // Form related states
  const [formOpen, setFormOpen] = useState(false);
  const [editingSetting, setEditingSetting] = useState<RewardSetting | null>(null);
  const [formData, setFormData] = useState<RewardSettingFormData>({
    amount: '',
    points: '',
    currency: 'AED',
    is_default: false,
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  // Confirmation dialog for setting as default
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [settingToMakeDefault, setSettingToMakeDefault] = useState<RewardSetting | null>(null);

  // API hooks
  const {
    data,
    isLoading,
    error,
    isError,
    refetch
  } = useListRewardSettingsQuery();

  const [addRewardSetting, { isLoading: isAdding }] = useAddRewardSettingMutation();
  const [updateRewardSetting, { isLoading: isUpdating }] = useUpdateRewardSettingMutation();
  const [deleteRewardSetting, { isLoading: isDeleting }] = useDeleteRewardSettingMutation();
  const [setDefaultRewardSetting, { isLoading: isSettingDefault }] = useSetDefaultRewardSettingMutation();

  // Handle API response structure
  const rewardSettings = Array.isArray(data?.data) ? data?.data : [];
  const totalRecords = rewardSettings.length;

  // Find current default setting
  const currentDefaultSetting = rewardSettings.find((setting: RewardSetting) => setting.is_default === 1);

  const isEditing = Boolean(editingSetting);
  const isFormLoading = isAdding || isUpdating;

  useEffect(() => {
    // Log the data structure for debugging
    if (data) {
      console.log('Reward settings data:', data);
    }
  }, [data]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Format currency display
  const formatCurrency = (amount: string, currencyCode: string) => {
    const symbol = currencySymbols[currencyCode] || currencyCode;
    return `${symbol} ${amount}`;
  };

  // Form handling functions
  const resetForm = () => {
    setFormData({
      amount: '',
      points: '',
      currency: 'AED',
      is_default: false,
    });
    setFormErrors({});
    setEditingSetting(null);
  };

  const handleAddSetting = () => {
    resetForm();
    // If there are no settings yet, set this as default by default
    if (rewardSettings.length === 0) {
      setFormData(prev => ({ ...prev, is_default: true }));
    }
    setFormOpen(true);
  };

  const handleEditSetting = (setting: RewardSetting) => {
    setFormData({
      amount: setting.amount || '',
      points: setting.points || '',
      currency: setting.currency || 'AED',
      is_default: setting.is_default === 1,
      id: setting.id,
    });
    setEditingSetting(setting);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    resetForm();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.amount.trim()) {
      newErrors.amount = 'Amount is required';
    } else if (!validateAmount(formData.amount)) {
      newErrors.amount = 'Invalid amount (must be a positive number)';
    }

    if (!formData.points.trim()) {
      newErrors.points = 'Points are required';
    } else if (!validatePoints(formData.points)) {
      newErrors.points = 'Invalid points (must be a positive number)';
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // If setting as default and there's already a default, show confirmation
    if (formData.is_default && currentDefaultSetting && 
        (!isEditing || (isEditing && editingSetting && editingSetting.id !== currentDefaultSetting.id))) {
      setSettingToMakeDefault({
        id: formData.id || 0,
        amount: formData.amount,
        points: formData.points,
        currency: formData.currency,
        is_default: 1,
      });
      setConfirmDialogOpen(true);
      return;
    }

    await saveRewardSetting();
  };

  const saveRewardSetting = async () => {
    try {
      let result;
      if (isEditing && editingSetting) {
        const updateData = {
          id: formData.id || editingSetting.id,
          amount: formData.amount.trim(),
          points: formData.points.trim(),
          currency: formData.currency.trim(),
          is_default: formData.is_default,
        };
        
        result = await updateRewardSetting(updateData).unwrap();
        setSnackbar({
          open: true,
          message: result?.message || 'Reward setting updated successfully!',
          severity: 'success'
        });
      } else {
        const addData = {
          amount: formData.amount.trim(),
          points: formData.points.trim(),
          currency: formData.currency.trim(),
          is_default: formData.is_default,
        };
        
        result = await addRewardSetting(addData).unwrap();
        setSnackbar({
          open: true,
          message: result?.message || 'Reward setting created successfully!',
          severity: 'success'
        });
      }
      handleFormClose();
      refetch();
    } catch (err: any) {
      console.error('Failed to save reward setting:', err);

      let errorMessage = `Error ${isEditing ? 'updating' : 'creating'} reward setting. Please try again.`;

      if (err?.data?.message) {
        errorMessage = err.data.message;
      } else if (err?.data?.error) {
        errorMessage = typeof err.data.error === 'string' ? err.data.error : 'Validation failed';
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const handleSetDefault = async (setting: RewardSetting) => {
    setSettingToMakeDefault(setting);
    setConfirmDialogOpen(true);
  };

  const confirmSetDefault = async () => {
    if (!settingToMakeDefault) return;

    try {
      const result = await setDefaultRewardSetting({ id: settingToMakeDefault.id }).unwrap();
      setSnackbar({
        open: true,
        message: result?.message || 'Default reward setting updated successfully!',
        severity: 'success'
      });
      setConfirmDialogOpen(false);
      setSettingToMakeDefault(null);
      refetch();
    } catch (err: any) {
      console.error('Failed to set default reward setting:', err);

      let errorMessage = 'Error setting default reward setting. Please try again.';

      if (err?.data?.message) {
        errorMessage = err.data.message;
      } else if (err?.data?.error) {
        errorMessage = typeof err.data.error === 'string' ? err.data.error : 'Operation failed';
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      setConfirmDialogOpen(false);
    }
  };

  const confirmSetDefaultFromForm = async () => {
    setConfirmDialogOpen(false);
    await saveRewardSetting();
  };

  const handleDelete = async (id: number) => {
    // Check if this is the default setting
    const settingToDelete = rewardSettings.find(setting => setting.id === id);
    if (settingToDelete?.is_default === 1) {
      setSnackbar({
        open: true,
        message: 'Cannot delete the default reward setting. Please set another as default first.',
        severity: 'error'
      });
      return;
    }

    if (window.confirm('Are you sure you want to delete this reward setting?')) {
      try {
        const result = await deleteRewardSetting({ id }).unwrap();
        setSnackbar({
          open: true,
          message: result?.message || 'Reward setting deleted successfully!',
          severity: 'success'
        });
        refetch();
      } catch (err: any) {
        console.error('Failed to delete reward setting:', err);

        let errorMessage = 'Error deleting reward setting. Please try again.';

        if (err?.data?.message) {
          errorMessage = err.data.message;
        } else if (err?.data?.error) {
          errorMessage = typeof err.data.error === 'string' ? err.data.error : 'Delete operation failed';
        } else if (err?.message) {
          errorMessage = err.message;
        }

        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setSettingToMakeDefault(null);
  };

  const renderErrorMessage = () => {
    if (error && 'status' in error) {
      switch (error.status) {
        case 403:
          return 'You don\'t have permission to view reward settings.';
        case 401:
          return 'Please sign in to continue.';
        case 404:
          return 'No reward settings found.';
        case 500:
          return 'Something went wrong. Please try again.';
        default:
          return 'Unable to load reward settings. Please try again.';
      }
    }
    return 'Network error. Please check your connection.';
  };

  // Filter settings based on search
  const filteredSettings = rewardSettings.filter((setting: RewardSetting) => {
    const matchesSearch = !searchStr || 
      setting.points?.toLowerCase().includes(searchStr.toLowerCase()) ||
      setting.amount?.toLowerCase().includes(searchStr.toLowerCase());
    
    return matchesSearch;
  });

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: shopifyColors.background }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        {/* Drawer for mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          
          {/* Page Header */}
          <Box sx={{ p: 3, pb: 2, backgroundColor: shopifyColors.surface }}>
            <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
              <Link color="inherit" href="#" underline="hover">
                <Home sx={{ fontSize: 20 }} />
              </Link>
              <Link color="inherit" href="#" underline="hover">
                Users
              </Link>
              <Typography color={shopifyColors.textPrimary} sx={{ fontWeight: 600 }}>
                Reward Settings
              </Typography>
            </Breadcrumbs>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="h4" sx={{ 
                  fontWeight: 700, 
                  color: shopifyColors.textPrimary,
                  mb: 1
                }}>
                  Reward Settings
                </Typography>
                <Typography variant="body1" sx={{ color: shopifyColors.textSecondary }}>
                  Configure reward point tiers based on purchase amounts (Currency: AED)
                  {currentDefaultSetting && (
                    <Chip
                      size="small"
                      label={`Default: ${currentDefaultSetting.points} points for ${formatCurrency(currentDefaultSetting.amount, currentDefaultSetting.currency || 'AED')}`}
                      sx={{ 
                        ml: 2,
                        backgroundColor: `${shopifyColors.success}15`,
                        color: shopifyColors.success,
                        border: `1px solid ${shopifyColors.success}30`,
                      }}
                    />
                  )}
                </Typography>
              </Box>
              
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddSetting}
                size="large"
                sx={{
                  backgroundColor: shopifyColors.primary,
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': {
                    backgroundColor: shopifyColors.primaryLight,
                  },
                }}
              >
                Add setting
              </Button>
            </Box>
          </Box>

          {/* Main Content Area */}
          <Box sx={{ flexGrow: 1, p: 3, pt: 2, overflow: 'auto' }}>
            {/* Filter Card */}
            <Card sx={{ mb: 3, borderRadius: '8px', border: `1px solid ${shopifyColors.border}` }}>
              <CardContent sx={{ p: '16px !important' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* Search */}
                  <TextField
                    size="small"
                    placeholder="Search by points or amount..."
                    value={searchStr}
                    onChange={(e) => setSearchStr(e.target.value)}
                    sx={{
                      minWidth: 250,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" sx={{ color: shopifyColors.textSecondary }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <Box sx={{ flexGrow: 1 }} />
                  
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    disabled={isLoading}
                    size="small"
                    sx={{
                      borderRadius: '6px',
                      textTransform: 'none',
                      borderColor: shopifyColors.border,
                      color: shopifyColors.textPrimary,
                    }}
                  >
                    Refresh
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Results Summary */}
            {!isLoading && !isError && (
              <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 2 }}>
                {filteredSettings.length} setting{filteredSettings.length === 1 ? '' : 's'}
                {searchStr && ` matching "${searchStr}"`} • Currency: AED
              </Typography>
            )}

            {/* Loading State */}
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress sx={{ color: shopifyColors.primary }} />
                <Typography sx={{ ml: 2, color: shopifyColors.textSecondary }}>Loading reward settings...</Typography>
              </Box>
            )}

            {/* Error State */}
            {isError && !isLoading && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  borderRadius: '6px',
                  border: `1px solid ${shopifyColors.error}20`,
                }}
                action={
                  <Button color="inherit" size="small" onClick={handleRefresh}>
                    Try again
                  </Button>
                }
              >
                {renderErrorMessage()}
              </Alert>
            )}

            {/* Empty State */}
            {!isLoading && !isError && filteredSettings.length === 0 && (
              <Card sx={{ textAlign: 'center', py: 8, borderRadius: '8px' }}>
                <SettingsIcon sx={{ fontSize: 64, color: shopifyColors.textSecondary, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" sx={{ color: shopifyColors.textPrimary, mb: 1, fontWeight: 600 }}>
                  {searchStr ? 'No settings found' : 'No reward settings yet'}
                </Typography>
                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 3 }}>
                  {searchStr 
                    ? `No settings match your search "${searchStr}"`
                    : 'Configure your first reward tier to get started'
                  }
                </Typography>
                {!searchStr && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAddSetting}
                    sx={{
                      backgroundColor: shopifyColors.primary,
                      borderRadius: '6px',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: shopifyColors.primaryLight,
                      },
                    }}
                  >
                    Add setting
                  </Button>
                )}
              </Card>
            )}

            {/* Settings List */}
            {!isLoading && filteredSettings.length > 0 && (
              <Card sx={{ borderRadius: '8px', border: `1px solid ${shopifyColors.border}`, overflow: 'hidden' }}>
                <List sx={{ p: 0 }}>
                  {filteredSettings.map((setting: RewardSetting, index: number) => (
                    <ListItem
                      key={setting.id}
                      sx={{
                        borderBottom: index < filteredSettings.length - 1 ? `1px solid ${shopifyColors.border}` : 'none',
                        '&:hover': {
                          backgroundColor: `${shopifyColors.background}80`,
                        },
                        py: 2.5,
                      }}
                    >
                      <ListItemIcon>
                        <Avatar
                          sx={{
                            bgcolor: setting.is_default === 1 ? shopifyColors.gold : shopifyColors.purple,
                            width: 48,
                            height: 48,
                          }}
                        >
                          {setting.is_default === 1 ? <Star /> : <SettingsIcon />}
                        </Avatar>
                      </ListItemIcon>
                      
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Paper
                              elevation={0}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 2,
                                py: 1,
                                backgroundColor: setting.is_default === 1 ? `${shopifyColors.gold}30` : `${shopifyColors.gold}20`,
                                borderRadius: '6px',
                              }}
                            >
                              <Stars sx={{ fontSize: 18, color: setting.is_default === 1 ? shopifyColors.warning : shopifyColors.gold }} />
                              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: shopifyColors.textPrimary }}>
                                {setting.points} Points
                              </Typography>
                            </Paper>
                            {setting.is_default === 1 ? (
                              <Chip
                                icon={<CheckCircle />}
                                label="Default"
                                size="small"
                                sx={{
                                  backgroundColor: `${shopifyColors.success}15`,
                                  color: shopifyColors.success,
                                  border: `1px solid ${shopifyColors.success}30`,
                                }}
                              />
                            ) : (
                              <Chip
                                label="Set as Default"
                                size="small"
                                onClick={() => handleSetDefault(setting)}
                                sx={{
                                  backgroundColor: `${shopifyColors.blue}15`,
                                  color: shopifyColors.blue,
                                  border: `1px solid ${shopifyColors.blue}30`,
                                  cursor: 'pointer',
                                  '&:hover': {
                                    backgroundColor: `${shopifyColors.blue}25`,
                                  },
                                }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 1, alignItems: 'center' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <MonetizationOn sx={{ fontSize: 16, color: shopifyColors.success }} />
                                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                                  Amount: {formatCurrency(setting.amount, setting.currency || 'AED')}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CurrencyExchange sx={{ fontSize: 16, color: shopifyColors.blue }} />
                                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                                  Currency: {setting.currency || 'AED'}
                                </Typography>
                              </Box>
                            </Box>
                            {setting.created_at && (
                              <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, display: 'block' }}>
                                Created: {formatDate(setting.created_at)}
                              </Typography>
                            )}
                            <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>
                              ID: {setting.id}
                            </Typography>
                          </Box>
                        }
                      />
                      
                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Tooltip title="Edit">
                            <IconButton
                              onClick={() => handleEditSetting(setting)}
                              size="small"
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Delete">
                            <IconButton
                              onClick={() => handleDelete(setting.id)}
                              disabled={isDeleting || setting.is_default === 1}
                              size="small"
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Card>
            )}
          </Box>
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={formOpen} 
        onClose={handleFormClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: '8px',
            border: `1px solid ${shopifyColors.border}`,
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          borderBottom: `1px solid ${shopifyColors.border}`,
          backgroundColor: shopifyColors.background,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
              {isEditing ? 'Edit reward setting' : 'Add reward setting'}
            </Typography>
            <IconButton 
              onClick={handleFormClose} 
              size="small" 
              disabled={isFormLoading}
              sx={{ color: shopifyColors.textSecondary }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Currency Display (Fixed as AED) */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  color: shopifyColors.textPrimary, 
                  fontWeight: 600,
                  mb: 1 
                }}>
                  Currency
                </Typography>
                
                <Paper
                  elevation={0}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    backgroundColor: shopifyColors.background,
                    borderRadius: '6px',
                    border: `1px solid ${shopifyColors.border}`,
                  }}
                >
                  <CurrencyExchange sx={{ color: shopifyColors.blue, fontSize: 24 }} />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
                      UAE Dirham (AED)
                    </Typography>
                    <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>
                      {currencySymbols['AED']} - United Arab Emirates Dirham
                    </Typography>
                  </Box>
                </Paper>
                <input type="hidden" name="currency" value="AED" />
              </Box>

              <Divider />

              {/* Amount */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  color: shopifyColors.textPrimary, 
                  fontWeight: 600,
                  mb: 2 
                }}>
                  Purchase Amount (AED)
                </Typography>
                
                <TextField
                  name="amount"
                  label="Amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  error={!!formErrors.amount}
                  helperText={formErrors.amount || "Purchase amount in AED to earn points"}
                  fullWidth
                  required
                  placeholder="1000"
                  disabled={isFormLoading}
                  type="number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ color: shopifyColors.textSecondary, fontWeight: 600 }}>
                          {currencySymbols['AED']}
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                    }
                  }}
                />
              </Box>

              <Divider />

              {/* Points */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  color: shopifyColors.textPrimary, 
                  fontWeight: 600,
                  mb: 2 
                }}>
                  Reward Points
                </Typography>
                
                <TextField
                  name="points"
                  label="Points"
                  value={formData.points}
                  onChange={handleInputChange}
                  error={!!formErrors.points}
                  helperText={formErrors.points || "Points earned for this amount"}
                  fullWidth
                  required
                  placeholder="100"
                  disabled={isFormLoading}
                  type="number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Stars sx={{ fontSize: 20, color: shopifyColors.gold }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                    }
                  }}
                />
              </Box>

              <Divider />

              {/* Set as Default */}
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_default || false}
                      onChange={handleCheckboxChange}
                      name="is_default"
                      color="primary"
                      disabled={isFormLoading}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
                        Set as Default Reward Setting
                      </Typography>
                      <Typography variant="caption" sx={{ color: shopifyColors.textSecondary, display: 'block' }}>
                        {formData.is_default 
                          ? 'This will be the default reward setting. Previous default will be removed.'
                          : 'Make this the default reward setting'
                        }
                      </Typography>
                    </Box>
                  }
                />
                
                {currentDefaultSetting && formData.is_default && 
                 (!isEditing || (isEditing && editingSetting && editingSetting.id !== currentDefaultSetting.id)) && (
                  <Alert 
                    severity="warning" 
                    icon={<Warning />}
                    sx={{ 
                      mt: 2,
                      borderRadius: '6px',
                      backgroundColor: `${shopifyColors.warning}15`,
                    }}
                  >
                    Current default setting: {currentDefaultSetting.points} points for {formatCurrency(currentDefaultSetting.amount, currentDefaultSetting.currency || 'AED')}
                  </Alert>
                )}
              </Box>
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ 
            p: 3, 
            pt: 2, 
            gap: 2,
            borderTop: `1px solid ${shopifyColors.border}`,
          }}>
            <Button 
              onClick={handleFormClose} 
              disabled={isFormLoading}
              variant="outlined"
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: shopifyColors.border,
                color: shopifyColors.textPrimary,
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isFormLoading || !formData.amount?.trim() || !formData.points?.trim()}
              sx={{
                backgroundColor: shopifyColors.primary,
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                '&:hover': {
                  backgroundColor: shopifyColors.primaryLight,
                },
              }}
            >
              {isFormLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  {isEditing ? 'Saving...' : 'Adding...'}
                </Box>
              ) : (
                isEditing ? 'Save changes' : 'Add setting'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Confirmation Dialog for Setting Default */}
      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        PaperProps={{
          sx: { 
            borderRadius: '8px',
            border: `1px solid ${shopifyColors.border}`,
          }
        }}
      >
        <ConfirmDialogTitle sx={{ 
          borderBottom: `1px solid ${shopifyColors.border}`,
          backgroundColor: shopifyColors.background,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning sx={{ color: shopifyColors.warning }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
              Set as Default
            </Typography>
          </Box>
        </ConfirmDialogTitle>
        
        <ConfirmDialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" sx={{ mb: 2, color: shopifyColors.textPrimary }}>
            Are you sure you want to set this as the default reward setting?
          </Typography>
          
          {currentDefaultSetting && (
            <Alert 
              severity="info"
              sx={{ 
                mb: 2,
                borderRadius: '6px',
              }}
            >
              Current default: {currentDefaultSetting.points} points for {formatCurrency(currentDefaultSetting.amount, currentDefaultSetting.currency || 'AED')}
              <br />
              This will be removed as default.
            </Alert>
          )}
          
          {settingToMakeDefault && (
            <Alert 
              severity="success"
              sx={{ 
                borderRadius: '6px',
              }}
            >
              New default: {settingToMakeDefault.points} points for {formatCurrency(settingToMakeDefault.amount, settingToMakeDefault.currency || 'AED')}
            </Alert>
          )}
        </ConfirmDialogContent>
        
        <ConfirmDialogActions sx={{ 
          p: 3, 
          pt: 2, 
          gap: 2,
          borderTop: `1px solid ${shopifyColors.border}`,
        }}>
          <Button 
            onClick={handleCloseConfirmDialog} 
            disabled={isSettingDefault}
            variant="outlined"
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 600,
              borderColor: shopifyColors.border,
              color: shopifyColors.textPrimary,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={settingToMakeDefault && settingToMakeDefault.id > 0 ? confirmSetDefault : confirmSetDefaultFromForm}
            variant="contained"
            disabled={isSettingDefault}
            sx={{
              backgroundColor: shopifyColors.primary,
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                backgroundColor: shopifyColors.primaryLight,
              },
            }}
          >
            {isSettingDefault ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Setting Default...
              </Box>
            ) : (
              'Set as Default'
            )}
          </Button>
        </ConfirmDialogActions>
      </ConfirmDialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            borderRadius: '6px',
            alignItems: 'center',
          }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RewardSettingsManagement;